import React, { useState, useEffect, useRef } from "react";
import html2canvas from "html2canvas";
import { API, WEDDING_DATE } from "../constants";
import { checkBingo } from "../utils/bingo";
import { useTutorial } from "../hooks/useTutorial";
import { CameraIcon, CloseIcon, DownloadIcon } from "../icons";
import ConfirmDialog from "../components/ConfirmDialog";
import TutorialOverlay from "../components/TutorialOverlay";
import BingoWin from "../components/BingoWin";
import "./BingoScreen.css";

const DOWNLOAD_CLASS = "bingo--downloading";

const TUTORIAL_STEPS = [
  { icon: "🎯", text: "Twoim zadaniem jest zrobić zdjęcia pasujące do opisów na <strong>kachelkach</strong>." },
  { icon: "📸", text: "Kliknij pustą kachelkę → wybierz zdjęcie z telefonu, aby ją wypełnić." },
  { icon: "🏆", text: "Uzupełnij <strong>4 kachelki w rzędzie, kolumnie lub ukosem</strong> → BINGO!" },
  { icon: "🖼️", text: "Twoje zdjęcia trafiają też do <strong>wspólnej galerii</strong> — zobaczą je wszyscy goście." },
  { icon: "💾", text: "Możesz pobrać swoją kartę bingo jako obrazek na pamiątkę." },
];

const WIN_SEEN_KEY = (userId) => `bingo_win_seen_${userId}`;

export default function BingoScreen({ userId, userName }) {
  const [card, setCard] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [confirmCell, setConfirmCell] = useState(null);
  const [previewCell, setPreviewCell] = useState(null);
  const [showWin, setShowWin] = useState(false);
  const fileRef  = useRef();
  const bingoRef = useRef();
  const tutorial = useTutorial("bingo");

  useEffect(() => {
    fetch(`${API}/api/bingo/${userId}`)
      .then((r) => r.json())
      .then(setCard)
      .catch(console.error);
  }, [userId]);

  // Show win animation only the very first time bingo is achieved.
  // Persisted in localStorage so switching tabs doesn't re-trigger it.
  useEffect(() => {
    if (!card) return;
    if (!checkBingo(card.cells)) return;
    if (localStorage.getItem(WIN_SEEN_KEY(userId))) return; // already seen
    localStorage.setItem(WIN_SEEN_KEY(userId), "1");
    setShowWin(true);
  }, [card, userId]);

  const openUpload = (cellIndex) => {
    setSelectedCell(cellIndex);
    fileRef.current.click();
  };

  const handleUpload = async (file) => {
    if (!file || selectedCell === null) return;
    setUploading(selectedCell);
    const fd = new FormData();
    fd.append("photo", file);
    fd.append("uploaderName", userName);
    try {
      const r = await fetch(`${API}/api/bingo/${userId}/cell/${selectedCell}`, {
        method: "POST",
        body: fd,
      });
      const data = await r.json();
      setCard((prev) => ({
        ...prev,
        cells: prev.cells.map((c, i) => (i === selectedCell ? data.cell : c)),
      }));
    } catch (e) {
      console.error(e);
    }
    setUploading(null);
    setSelectedCell(null);
  };

  const handleDeleteCell = async (cellIndex) => {
    const cell = card.cells[cellIndex];
    try {
      await fetch(`${API}/api/gallery/${cell.photoId}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uploaderId: userId }),
      });
      setCard((prev) => ({
        ...prev,
        cells: prev.cells.map((c, i) =>
          i === cellIndex ? { ...c, photoUrl: null, photoId: null, completed: false } : c
        ),
      }));
    } catch (e) {
      console.error(e);
    }
  };

  const downloadBingo = async () => {
    if (!bingoRef.current) return;
    setDownloading(true);
    bingoRef.current.classList.add(DOWNLOAD_CLASS);
    try {
      const canvas = await html2canvas(bingoRef.current, {
        useCORS: true,
        scale: 2,
        backgroundColor: "#3d2b1f",
        // html2canvas doesn't support object-fit: cover natively.
        // onclone fixes each cell image so it fills the cell correctly.
        onclone: (_doc, el) => {
          el.querySelectorAll(".bingo__cell-img").forEach((img) => {
            img.style.position  = "absolute";
            img.style.inset     = "0";
            img.style.width     = "100%";
            img.style.height    = "100%";
            img.style.objectFit = "cover";
          });
        },
      });
      const link = document.createElement("a");
      link.download = `bingo-${userName}-wesele.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) {
      console.error(e);
      alert("Błąd przy pobieraniu. Spróbuj ponownie.");
    }
    bingoRef.current?.classList.remove(DOWNLOAD_CLASS);
    setDownloading(false);
  };

  if (!card) {
    return (
      <div className="screen">
        <div className="loading">Ładowanie bingo...</div>
      </div>
    );
  }

  const hasBingo = checkBingo(card.cells);
  const completed = card.cells.filter((c) => c.completed).length;

  return (
    <div className="screen">
      {/* ── Header ── */}
      <div className="page-header">
        <h1 className="page-header__title">Bingo</h1>
        <p className="page-header__sub">
          {completed}/16 zdjęć
          {hasBingo ? (
            <button className="bingo__celebrate-btn" onClick={() => setShowWin(true)}>
              🎉 BINGO!
            </button>
          ) : " · Zbierz zdjęcia!"}
        </p>
        <button className="help-btn" onClick={tutorial.reopen} title="Pomoc">?</button>
      </div>

      {/* ── Scrollable body ── */}
      <div className="screen__body">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={(e) => {
            if (e.target.files[0]) handleUpload(e.target.files[0]);
            e.target.value = "";
          }}
        />

        <div ref={bingoRef} className="bingo__container">
          <div className="bingo__header">
            {"BINGO".split("").map((l) => (
              <span key={l} className="bingo__letter">{l}</span>
            ))}
          </div>

          <div className="bingo__grid">
            {card.cells.map((cell, i) => (
              <div
                key={i}
                className={[
                  "bingo__cell",
                  cell.completed ? "bingo__cell--done" : "",
                  uploading === i ? "bingo__cell--uploading" : "",
                ].filter(Boolean).join(" ")}
                onClick={() => {
                  if (cell.completed) { setPreviewCell(i); return; }
                  if (uploading === null) openUpload(i);
                }}
              >
                {cell.completed ? (
                  <>
                    <img
                      src={`${API}${cell.photoUrl}`}
                      alt=""
                      className="bingo__cell-img"
                      onError={(e) => { e.target.style.display = "none"; }}
                    />
                    <div className="bingo__cell-label">{cell.category}</div>
                  </>
                ) : uploading === i ? (
                  <div className="bingo__cell-uploading-icon">⏳</div>
                ) : (
                  <>
                    <div className="bingo__cell-icon"><CameraIcon /></div>
                    <div className="bingo__cell-label-empty">{cell.category}</div>
                  </>
                )}
              </div>
            ))}
          </div>

          <div className="bingo__footer">
            <span className="bingo__footer-name">{userName}</span>
            <span className="bingo__footer-wedding">💍 Wesele {WEDDING_DATE}</span>
          </div>
        </div>

        <div className="bingo__download-section">
          <button className="bingo__download-btn" onClick={downloadBingo} disabled={downloading}>
            <span className="bingo__download-icon"><DownloadIcon /></span>
            {downloading ? "Pobieranie..." : "Pobierz moje Bingo"}
          </button>
        </div>
      </div>

      {/* ── Photo preview lightbox ── */}
      {previewCell !== null && card?.cells[previewCell]?.photoUrl && (
        <div className="lightbox" onClick={() => setPreviewCell(null)}>
          <div className="lightbox__content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox__close" onClick={() => setPreviewCell(null)}>
              <CloseIcon />
            </button>
            <img
              src={`${API}${card.cells[previewCell].photoUrl}`}
              alt=""
              className="lightbox__img"
            />
            <div className="lightbox__meta">
              <span className="lightbox__name">{card.cells[previewCell].category}</span>
              <button
                className="lightbox__delete-btn"
                onClick={() => { setPreviewCell(null); setConfirmCell(previewCell); }}
              >
                <CloseIcon /> Usuń
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Win animation (fires once per bingo, click to dismiss) ── */}
      {showWin && <BingoWin onDismiss={() => setShowWin(false)} />}

      {confirmCell !== null && (
        <ConfirmDialog
          icon="📸"
          title="Usunąć zdjęcie?"
          message={`Usunąć zdjęcie z kachelki „${card.cells[confirmCell]?.category}"?`}
          confirmLabel="Usuń"
          cancelLabel="Anuluj"
          onConfirm={() => { const idx = confirmCell; setConfirmCell(null); handleDeleteCell(idx); }}
          onCancel={() => setConfirmCell(null)}
        />
      )}

      {tutorial.visible && (
        <TutorialOverlay title="Weselne Bingo 🎯" steps={TUTORIAL_STEPS} onClose={tutorial.dismiss} />
      )}
    </div>
  );
}
